/**
 * De un horario a una lista de huecos.
 *
 * Es la única pieza difícil de toda la agenda, así que está sola y sin base de
 * datos dentro: entra lo que dice el horario, lo que ya está ocupado y qué hora
 * es, y sale una lista. Todo lo demás —endpoints, panel, correos— es fontanería
 * alrededor de esta función, y sólo se puede escribir con confianza si esta se
 * puede probar sin levantar nada.
 *
 * Dos reglas que explican casi todas las decisiones del archivo:
 *
 *   1. El horario se guarda en hora LOCAL del negocio; lo ocupado y lo que sale
 *      son instantes absolutos. Un horario en UTC se movería solo al entrar el
 *      horario de verano, y la estética no abre una hora antes por eso.
 *
 *   2. Esto corre SIEMPRE en el servidor. Si el navegador calculara los huecos,
 *      la agenda cambiaría según dónde esté el visitante — quien reserve desde
 *      Madrid vería la peluquería de Hermosillo abierta de madrugada.
 */
/**
 * El tramo más largo de lugares libres seguidos entre `0` y `capacity`.
 *
 * Los intervalos sin `seatStart` (un evento del calendario externo) cierran
 * todo; los que lo tienen ocupan `[seatStart, seatStart + seats)`. Un intervalo
 * propio sin `seatStart` —datos de antes de los grupos— cuenta como ocupado
 * desde el primer lugar libre, que es donde lo habría puesto el servidor.
 */
export function largestFreeRun(capacity, taken) {
    if (capacity <= 0)
        return 0;
    const used = new Array(capacity).fill(false);
    for (const interval of taken) {
        if (interval.seatStart === undefined) {
            if (interval.seats >= capacity)
                return 0;
            let left = interval.seats;
            for (let seat = 0; seat < capacity && left > 0; seat++) {
                if (!used[seat]) {
                    used[seat] = true;
                    left--;
                }
            }
            continue;
        }
        const end = Math.min(capacity, interval.seatStart + interval.seats);
        for (let seat = Math.max(0, interval.seatStart); seat < end; seat++)
            used[seat] = true;
    }
    let best = 0;
    let run = 0;
    for (const seat of used) {
        run = seat ? 0 : run + 1;
        if (run > best)
            best = run;
    }
    return best;
}
const MINUTE_MS = 60_000;
const DAY_MS = 86_400_000;
const MAX_DAYS = 366;
// ── Zonas horarias ───────────────────────────────────────────────────────────
// Sin librería: `Intl` ya lleva la base de datos de zonas, y en Workers pesa
// cero. Lo único que hay que construir a mano es el camino de vuelta —de hora
// local a instante—, que `Intl` no da.
const formatters = new Map();
function formatter(timezone) {
    let cached = formatters.get(timezone);
    if (!cached) {
        cached = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            // `h23` y no `hour12: false`: con este último, algunos motores escriben
            // «24» para la medianoche y la cuenta se va un día entero.
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        formatters.set(timezone, cached);
    }
    return cached;
}
/** Qué hora local marca el reloj del negocio en ese instante. */
function zonedFields(ts, timezone) {
    const parts = formatter(timezone).formatToParts(new Date(ts));
    const read = (type) => {
        const part = parts.find((p) => p.type === type);
        return part ? Number(part.value) : 0;
    };
    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        minuteOfDay: read('hour') * 60 + read('minute'),
        second: read('second'),
    };
}
/** Cuánto se separa la zona de UTC en ese instante, en ms. */
function offsetAt(ts, timezone) {
    const f = zonedFields(ts, timezone);
    const asIfUtc = Date.UTC(f.year, f.month - 1, f.day, 0, f.minuteOfDay, f.second);
    return asIfUtc - ts;
}
/**
 * El camino de vuelta: qué instante es esa hora del reloj del negocio.
 *
 * Dos pasadas porque el desplazamiento que hay que restar es el del instante
 * RESULTANTE, no el del que se adivina primero, y los dos días del año en que
 * cambia la hora no coinciden.
 *
 * Devuelve `null` cuando esa hora no existió. Pasa una vez al año: al adelantar
 * el reloj, el salto se come una hora entera de la que no hay ningún instante,
 * y un hueco a las 2:30 de ese domingo no se puede vender.
 */
export function instantFromLocal(year, month, day, minuteOfDay, timezone) {
    // `Date.UTC` normaliza solo: un `minuteOfDay` de 1500 pasa al día siguiente.
    const naive = Date.UTC(year, month - 1, day, 0, minuteOfDay);
    const firstGuess = naive - offsetAt(naive, timezone);
    const ts = naive - offsetAt(firstGuess, timezone);
    const target = new Date(naive);
    const actual = zonedFields(ts, timezone);
    const matches = actual.year === target.getUTCFullYear() &&
        actual.month === target.getUTCMonth() + 1 &&
        actual.day === target.getUTCDate() &&
        actual.minuteOfDay === target.getUTCHours() * 60 + target.getUTCMinutes();
    return matches ? ts : null;
}
// ── Calendario ───────────────────────────────────────────────────────────────
// Fechas locales como texto `YYYY-MM-DD`. No pasan por `Date` con zona en
// ningún momento: un día del calendario no tiene hora y darle una es la forma
// clásica de que un rango se corra 24 horas.
function parseDay(day) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (!match)
        return null;
    return { year: Number(match[1]), month: Number(match[2]), date: Number(match[3]) };
}
function formatDay(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
/** Qué día de la semana cae una fecha del calendario. Sin zona: no hace falta. */
function weekdayOf(year, month, date) {
    return new Date(Date.UTC(year, month - 1, date)).getUTCDay();
}
/** Ordena, fusiona lo que se toca y tira lo vacío. */
function normalize(windows) {
    const sorted = windows
        .filter((w) => w.end > w.start)
        .sort((a, b) => a.start - b.start);
    const merged = [];
    for (const window of sorted) {
        const last = merged[merged.length - 1];
        if (last && window.start <= last.end) {
            last.end = Math.max(last.end, window.end);
        }
        else {
            merged.push({ ...window });
        }
    }
    return merged;
}
/** Le quita un trozo a cada franja; una franja partida por el medio da dos. */
function subtract(windows, cut) {
    const result = [];
    for (const window of windows) {
        if (cut.end <= window.start || cut.start >= window.end) {
            result.push(window);
            continue;
        }
        if (cut.start > window.start)
            result.push({ start: window.start, end: cut.start });
        if (cut.end < window.end)
            result.push({ start: cut.end, end: window.end });
    }
    return result;
}
/**
 * Las franjas abiertas de un día: la semana, menos lo que cierra, más lo que
 * abre.
 *
 * Ese orden importa. Cerrar primero y abrir después es lo que permite decir «el
 * jueves cierro por la tarde, pero ese jueves sí atiendo de 6 a 8» sin que las
 * dos excepciones se anulen entre sí.
 */
export function windowsForDay(day, weekday, rules, exceptions) {
    const forDay = exceptions.filter((e) => e.day === day);
    // Un `closed` sin rango cierra el día entero, gane lo que gane después.
    if (forDay.some((e) => e.kind === 'closed' && e.startMinute === null))
        return [];
    let windows = normalize(rules
        .filter((r) => r.weekday === weekday)
        .map((r) => ({ start: r.startMinute, end: r.endMinute })));
    for (const exception of forDay) {
        if (exception.kind !== 'closed')
            continue;
        if (exception.startMinute === null || exception.endMinute === null)
            continue;
        windows = subtract(windows, { start: exception.startMinute, end: exception.endMinute });
    }
    for (const exception of forDay) {
        if (exception.kind !== 'open')
            continue;
        if (exception.startMinute === null || exception.endMinute === null)
            continue;
        windows.push({ start: exception.startMinute, end: exception.endMinute });
    }
    return normalize(windows);
}
// ── El motor ─────────────────────────────────────────────────────────────────
/**
 * Los huecos que se pueden vender entre dos fechas.
 *
 * Sobre los márgenes y el cupo: el hueco candidato se ensancha con los márgenes
 * antes de mirar qué lo pisa, y todo lo que lo pise consume lugares. Con cupo 1
 * —la cita normal— eso es exactamente lo que se quiere. Con cupo N y márgenes a
 * la vez, una clase pegada a otra le come lugares a su vecina; es una
 * simplificación consciente, porque un salón que separa sus clases lo hace en
 * el horario y no con márgenes. Si algún día hace falta, la salida es contar
 * lugares sobre el hueco sin ensanchar y dejar los márgenes sólo para el
 * conflicto — pero no antes de que alguien lo pida.
 */
export function computeSlots(input) {
    const { timezone, rules, exceptions, busy, durationMinutes, bufferBeforeMinutes = 0, bufferAfterMinutes = 0, capacity = 1, partySize = 1, minNoticeMinutes = 0, maxAdvanceDays = 60, now, fromDay, toDay, } = input;
    if (durationMinutes <= 0 || capacity <= 0)
        return [];
    const step = input.stepMinutes && input.stepMinutes > 0 ? input.stepMinutes : durationMinutes;
    const from = parseDay(fromDay);
    const to = parseDay(toDay);
    if (!from || !to)
        return [];
    const firstDayMs = Date.UTC(from.year, from.month - 1, from.date);
    const lastDayMs = Date.UTC(to.year, to.month - 1, to.date);
    if (lastDayMs < firstDayMs)
        return [];
    // Un rango abierto de par en par recorrería años día a día por nada.
    const totalDays = Math.min(Math.floor((lastDayMs - firstDayMs) / DAY_MS) + 1, MAX_DAYS);
    const earliest = now + minNoticeMinutes * MINUTE_MS;
    const latest = now + maxAdvanceDays * DAY_MS;
    const slots = [];
    for (let offset = 0; offset < totalDays; offset++) {
        const dayMs = firstDayMs + offset * DAY_MS;
        const dayDate = new Date(dayMs);
        const year = dayDate.getUTCFullYear();
        const month = dayDate.getUTCMonth() + 1;
        const date = dayDate.getUTCDate();
        const windows = windowsForDay(formatDay(dayMs), weekdayOf(year, month, date), rules, exceptions);
        for (const window of windows) {
            for (let minute = window.start; minute + durationMinutes <= window.end; minute += step) {
                const startsAt = instantFromLocal(year, month, date, minute, timezone);
                // La hora que se comió el cambio de horario. No existe, no se vende.
                if (startsAt === null)
                    continue;
                // La duración se cuenta en minutos reales y no en minutos del reloj: una
                // hora de consulta dura una hora aunque el reloj salte por el medio.
                const endsAt = startsAt + durationMinutes * MINUTE_MS;
                if (startsAt < earliest || startsAt > latest)
                    continue;
                const blockedFrom = startsAt - bufferBeforeMinutes * MINUTE_MS;
                const blockedTo = endsAt + bufferAfterMinutes * MINUTE_MS;
                let taken = 0;
                const overlapping = [];
                for (const interval of busy) {
                    if (interval.endsAt <= blockedFrom || interval.startsAt >= blockedTo)
                        continue;
                    taken += interval.seats;
                    overlapping.push(interval);
                }
                const seatsLeft = capacity - taken;
                if (seatsLeft <= 0)
                    continue;
                /* Con cupo 1 no hay nada que contar: si queda un lugar, cabe uno. */
                const maxParty = capacity === 1 ? 1 : Math.min(seatsLeft, largestFreeRun(capacity, overlapping));
                if (maxParty < Math.max(1, partySize))
                    continue;
                slots.push({
                    startsAt: new Date(startsAt).toISOString(),
                    endsAt: new Date(endsAt).toISOString(),
                    seatsLeft,
                    maxParty,
                });
            }
        }
    }
    // Las franjas de un día vienen ordenadas, pero un `open` de madrugada puede
    // dejar el día desordenado respecto al anterior. Quien lo pinta espera orden.
    return slots.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}
/** El instante en que empieza un día local. Si la medianoche no existe (cambio de horario), la primera hora que sí. */
function startOfLocalDay(year, month, date, timezone) {
    for (const minute of [0, 60, 120]) {
        const at = instantFromLocal(year, month, date, minute, timezone);
        if (at !== null)
            return at;
    }
    return null;
}
/**
 * Los días en que puede empezar una reserva de `days` días seguidos.
 *
 * Un día cuenta si el horario lo tiene abierto (alguna franja ese día de la
 * semana, sin una excepción que lo cierre): para un servicio por días, el
 * horario dice QUÉ días se trabaja, no a qué hora. Todos los días del tramo
 * tienen que estar abiertos —no se puede rentar del viernes al lunes si el
 * domingo no se entrega—.
 *
 * El hueco va de la medianoche del primer día a la del día siguiente al
 * último, en la zona del negocio. Así una cita de horas ese mismo día lo
 * ocupa, y al revés, sin una regla aparte.
 */
export function computeDaySlots(input) {
    const { timezone, rules, exceptions, busy, capacity = 1, partySize = 1, minNoticeMinutes = 0, maxAdvanceDays = 60, now } = input;
    const days = Math.max(1, Math.floor(input.days));
    if (capacity <= 0)
        return [];
    const from = parseDay(input.fromDay);
    const to = parseDay(input.toDay);
    if (!from || !to)
        return [];
    const firstDayMs = Date.UTC(from.year, from.month - 1, from.date);
    const lastDayMs = Date.UTC(to.year, to.month - 1, to.date);
    if (lastDayMs < firstDayMs)
        return [];
    const totalDays = Math.min(Math.floor((lastDayMs - firstDayMs) / DAY_MS) + 1, MAX_DAYS);
    /* El primer día que se puede elegir: el del aviso mínimo, en la zona del
       negocio. Hoy cuenta si el aviso lo permite. */
    const earliest = zonedFields(now + minNoticeMinutes * MINUTE_MS, timezone);
    const earliestDay = Date.UTC(earliest.year, earliest.month - 1, earliest.day);
    const latest = now + maxAdvanceDays * DAY_MS;
    const openCache = new Map();
    const isOpen = (dayMs) => {
        const hit = openCache.get(dayMs);
        if (hit !== undefined)
            return hit;
        const d = new Date(dayMs);
        const open = windowsForDay(formatDay(dayMs), weekdayOf(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate()), rules, exceptions).length > 0;
        openCache.set(dayMs, open);
        return open;
    };
    const slots = [];
    for (let offset = 0; offset < totalDays; offset++) {
        const dayMs = firstDayMs + offset * DAY_MS;
        if (dayMs < earliestDay)
            continue;
        let allOpen = true;
        for (let k = 0; k < days; k++) {
            if (!isOpen(dayMs + k * DAY_MS)) {
                allOpen = false;
                break;
            }
        }
        if (!allOpen)
            continue;
        const first = new Date(dayMs);
        const after = new Date(dayMs + days * DAY_MS);
        const startsAt = startOfLocalDay(first.getUTCFullYear(), first.getUTCMonth() + 1, first.getUTCDate(), timezone);
        const endsAt = startOfLocalDay(after.getUTCFullYear(), after.getUTCMonth() + 1, after.getUTCDate(), timezone);
        if (startsAt === null || endsAt === null || startsAt > latest)
            continue;
        let taken = 0;
        const overlapping = [];
        for (const interval of busy) {
            if (interval.endsAt <= startsAt || interval.startsAt >= endsAt)
                continue;
            taken += interval.seats;
            overlapping.push(interval);
        }
        const seatsLeft = capacity - taken;
        if (seatsLeft <= 0)
            continue;
        const maxParty = capacity === 1 ? 1 : Math.min(seatsLeft, largestFreeRun(capacity, overlapping));
        if (maxParty < Math.max(1, partySize))
            continue;
        slots.push({ startsAt: new Date(startsAt).toISOString(), endsAt: new Date(endsAt).toISOString(), seatsLeft, maxParty });
    }
    return slots;
}
//# sourceMappingURL=availability.js.map